// components/members/PresidentDetailPage.js
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Image } from 'react-bootstrap';
import { Files, Search } from 'react-bootstrap-icons';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MemberFeedItem from './MemberFeedItem';
import '../../App.css';
import './PresidentDetailPage.css';

const SCHEMA = 'united_states_of_america';

const formatDate = (inputDate) => {
  const date = new Date(inputDate);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

const getOrdinalSuffix = (number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = number % 100;
  return number + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

function PresidentDetailPage() {
  const { t } = useTranslation('common');
  const { id } = useParams();
  const location = useLocation();

  // Get data from navigation state
  const name = location.state?.name;
  const termStart = location.state?.termStart;
  const termEnd = location.state?.termEnd;
  const presidentNumber = location.state?.presidentNumber;
  const imageSource = location.state?.imageSource;
  const description = location.state?.description || '';

  const [executiveOrders, setExecutiveOrders] = useState(null);
  const [executiveOrdersModal, setExecutiveOrdersModal] = useState(null);
  const [presidentialDocs, setPresidentialDocs] = useState(null);
  const [presidentialDocsModal, setPresidentialDocsModal] = useState(null);
  const [stateOfUnion, setStateOfUnion] = useState(null);
  const [stateOfUnionModal, setStateOfUnionModal] = useState(null);
  const [laws, setLaws] = useState(null);
  const [lawsModal, setLawsModal] = useState(null);

  const [showBioModal, setShowBioModal] = useState(false);
  const [showEOModal, setShowEOModal] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [showSOTUModal, setShowSOTUModal] = useState(false);
  const [showLawsModal, setShowLawsModal] = useState(false);

  const [searchQueryModal, setSearchQueryModal] = useState('');
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);

  const [error, setError] = useState(null);
  const [error2, setError2] = useState(null);
  const [error3, setError3] = useState(null);
  const [error4, setError4] = useState(null);

  const apiKey = process.env.REACT_APP_API_KEY;
  const requestOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
  };

  // Translated label reused across all five sections' empty-state and error displays
  const currentlyUnavailableLabel = t('common:currentlyUnavailable', 'Currently Unavailable');

  const truncateDescription = (desc, maxLength) => {
    if (!desc) return '';
    if (desc.length > maxLength) {
      return desc.substring(0, maxLength) + '...';
    }
    return desc;
  };

  const truncatedDescription = truncateDescription(description, 200);

  const renderEndTerm = () => {
    const formattedEndTerm = formatDate(termEnd);
    if (formattedEndTerm === '12-31-1969' || !termEnd) {
      return t('common:present', 'Present');
    }
    return formattedEndTerm;
  };

  // Fetch Executive Orders
  useEffect(() => {
    const fetchExecutiveOrders = async () => {
      try {
        const url = `https://api.govglance.org/members/executive_orders?order_by=created_at&limit=2&skip=0&president=${name}`;
        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error('Currently Unavailable');
        const data = await response.json();
        setExecutiveOrders(data);
      } catch (err) {
        setError(err);
      }
    };

    const fetchExecutiveOrdersModal = async () => {
      try {
        const url = `https://api.govglance.org/members/executive_orders?order_by=created_at&limit=300&skip=0&president=${name}`;
        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error('Currently Unavailable');
        const data = await response.json();
        setExecutiveOrdersModal(data);
      } catch (err) {
        setError(err);
      }
    };

    if (name) {
      fetchExecutiveOrders();
      fetchExecutiveOrdersModal();
    }
  }, [name]);

  // Fetch Presidential Documents
  useEffect(() => {
    const fetchPresidentialDocs = async () => {
      try {
        const url = `https://api.govglance.org/members/presidential_documents?order_by=created_at&limit=2&skip=0&president=${name}`;
        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error('Currently Unavailable');
        const data = await response.json();
        setPresidentialDocs(data);
      } catch (err) {
        setError2(err);
      }
    };

    const fetchPresidentialDocsModal = async () => {
      try {
        const url = `https://api.govglance.org/members/presidential_documents?order_by=created_at&limit=300&skip=0&president=${name}`;
        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error('Currently Unavailable');
        const data = await response.json();
        setPresidentialDocsModal(data);
      } catch (err) {
        setError2(err);
      }
    };

    if (name) {
      fetchPresidentialDocs();
      fetchPresidentialDocsModal();
    }
  }, [name]);

  // Fetch State of the Union
  useEffect(() => {
    const fetchStateOfUnion = async () => {
      try {
        const url = `https://api.govglance.org/members/state_of_the_union?order_by=created_at&limit=1&skip=0&president=${name}`;
        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error('Currently Unavailable');
        const data = await response.json();
        setStateOfUnion(data);
      } catch (err) {
        setError3(err);
      }
    };

    const fetchStateOfUnionModal = async () => {
      try {
        const url = `https://api.govglance.org/members/state_of_the_union?order_by=created_at&limit=10&skip=0&president=${name}`;
        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error('Currently Unavailable');
        const data = await response.json();
        setStateOfUnionModal(data);
      } catch (err) {
        setError3(err);
      }
    };

    if (name) {
      fetchStateOfUnion();
      fetchStateOfUnionModal();
    }
  }, [name]);

  // Fetch Laws
  useEffect(() => {
    const fetchLaws = async () => {
      try {
        const url = `https://api.govglance.org/members/public_private_laws?limit=2&skip=0&president=${name}`;
        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error('Currently Unavailable');
        const data = await response.json();
        setLaws(data);
      } catch (err) {
        setError4(err);
      }
    };

    const fetchLawsModal = async () => {
      try {
        const url = `https://api.govglance.org/members/public_private_laws?limit=50&skip=0&president=${name}`;
        const response = await fetch(url, requestOptions);
        if (!response.ok) throw new Error('Currently Unavailable');
        const data = await response.json();
        setLawsModal(data);
      } catch (err) {
        setError4(err);
      }
    };

    if (name) {
      fetchLaws();
      fetchLawsModal();
    }
  }, [name]);

  /**
   * Shared card list. One MemberFeedItem per row.
   *
   * MemberFeedItem reads created_at for the date and document_link /
   * download.pdfLink for the doc button, so each section maps its own field
   * names onto those before handing the record over.
   *
   * feedName/branch/topic stay English per stored-data convention.
   */
  const renderCards = (data, { table, feedName, topic, buttonName, normalize }) => {
    if (!data || data.length === 0) {
      return <p className="president-empty">{currentlyUnavailableLabel}</p>;
    }

    return (
      <div className="president-card-list">
        {data.map((item, index) => (
          <MemberFeedItem
            key={item.id ?? index}
            item={normalize ? normalize(item) : item}
            schema={SCHEMA}
            table={table}
            feedName={feedName}
            branch="Executive"
            topic={topic}
            buttonName={buttonName}
            variant="default"
            detailRoute={false}
          />
        ))}
      </div>
    );
  };

  const renderExecutiveOrders = (data, isModal = false) => {
    const filteredData = isModal && isSearchTriggered && data
      ? data.filter(item => item.title.toLowerCase().includes(searchQueryModal.toLowerCase()))
      : data;

    return renderCards(filteredData, {
      table: 'executive_orders',
      feedName: 'Executive Orders',
      topic: 'Presidents',
      buttonName: t('common:order', 'Order'),
      normalize: (item) => ({ ...item, document_link: item.pdf_url || null }),
    });
  };

  const renderPresidentialDocs = (data) => renderCards(data, {
    table: 'presidential_documents',
    feedName: 'Presidential Documents',
    topic: 'Presidents',
    buttonName: t('common:doc', 'Doc'),
    normalize: (item) => ({ ...item, created_at: item.date_issued }),
  });

  const renderStateOfUnion = (data) => renderCards(data, {
    table: 'state_of_the_union',
    feedName: 'State of the Union',
    topic: 'Presidents',
    buttonName: null,
  });

  const renderLaws = (data) => renderCards(data, {
    table: 'public_private_laws',
    feedName: 'Laws',
    topic: 'Presidents',
    buttonName: null,
    normalize: (item) => ({ ...item, created_at: item.date_issued }),
  });

  return (
    <div id='feedDisplay' className="president-detail-page">
      {/* President Header */}
      <div className="president-header">
        {imageSource && (
          <div className="president-profile-image">
            <Image id='profileImage-image' src={imageSource} alt="" />
          </div>
        )}

        <div className="president-profile-info">
          <div className="president-name">
            <span>{name}</span>
          </div>
          <div className="president-title">
            <span>{t('common:nthUSPresident', '{{ordinal}} U.S. President', { ordinal: getOrdinalSuffix(presidentNumber) })}</span>
          </div>
          <div className="president-term">
            <span>{t('common:termRange', 'From {{start}} To {{end}}', { start: formatDate(termStart), end: renderEndTerm() })}</span>
          </div>
        </div>
      </div>

      {/* Biography */}
      {description && (
        <div className="president-section">
          <div className="president-biography" dangerouslySetInnerHTML={{ __html: truncatedDescription }} />
          <Button id='repsViewMore' onClick={() => setShowBioModal(true)}>
            <Files size={20} /> {t('common:readMore', 'Read More')}
          </Button>
        </div>
      )}

      {/* State of the Union */}
      <div className="president-section">
        <div className="president-section-heading" onClick={() => setShowSOTUModal(true)}>
          {t('common:stateOfTheUnionAddress', 'State of the Union Address')}
        </div>
        {error3 ? <p className="president-empty">{currentlyUnavailableLabel}</p> : renderStateOfUnion(stateOfUnion)}
        <Button id='repsViewMore' onClick={() => setShowSOTUModal(true)}>
          <Files size={20} /> {t('common:viewMore', 'View More')}
        </Button>
      </div>

      {/* Executive Orders */}
      <div className="president-section">
        <div className="president-section-heading" onClick={() => setShowEOModal(true)}>
          {t('common:executiveOrders', 'Executive Orders')}
        </div>
        {error ? <p className="president-empty">{currentlyUnavailableLabel}</p> : renderExecutiveOrders(executiveOrders)}
        <Button id='repsViewMore' onClick={() => setShowEOModal(true)}>
          <Files size={20} /> {t('common:viewMore', 'View More')}
        </Button>
      </div>

      {/* Presidential Documents */}
      <div className="president-section">
        <div className="president-section-heading" onClick={() => setShowDocsModal(true)}>
          {t('common:presidentialDocuments', 'Presidential Documents')}
        </div>
        {error2 ? <p className="president-empty">{currentlyUnavailableLabel}</p> : renderPresidentialDocs(presidentialDocs)}
        <Button id='repsViewMore' onClick={() => setShowDocsModal(true)}>
          <Files size={20} /> {t('common:viewMore', 'View More')}
        </Button>
      </div>

      {/* Laws */}
      <div className="president-section">
        <div className="president-section-heading" onClick={() => setShowLawsModal(true)}>
          {t('common:laws', 'Laws')}
        </div>
        {error4 ? <p className="president-empty">{currentlyUnavailableLabel}</p> : renderLaws(laws)}
        <Button id='repsViewMore' onClick={() => setShowLawsModal(true)}>
          <Files size={20} /> {t('common:viewMore', 'View More')}
        </Button>
      </div>

      <div className="president-page-tail" />

      {/* Modals */}
      <Modal id='fullBillModal' size="xl" show={showBioModal} onHide={() => setShowBioModal(false)}>
        <Modal.Header id='billModalHeader' closeButton>
          <h3>{t('common:biographyTitle', "{{name}}'s Biography", { name })}</h3>
        </Modal.Header>
        <div id='fullBillFeed'>
          <div className="president-modal-content" dangerouslySetInnerHTML={{ __html: description }} />
        </div>
      </Modal>

      <Modal id='fullBillModal' size="xl" show={showSOTUModal} onHide={() => setShowSOTUModal(false)}>
        <Modal.Header id='billModalHeader' closeButton>
          <h3>{t('common:presidentSotuTitle', "{{name}}'s State of the Union Addresses", { name })}</h3>
        </Modal.Header>
        <div id='fullBillFeed'>
          {renderStateOfUnion(stateOfUnionModal)}
        </div>
      </Modal>

      <Modal id='fullBillModal' size="xl" show={showEOModal} onHide={() => setShowEOModal(false)}>
        <Modal.Header id='billModalHeader' closeButton>
          <h3>{t('common:presidentExecutiveOrdersTitle', "{{name}}'s Executive Orders", { name })}</h3>
        </Modal.Header>
        <div id='searchForm'>
          <Form className="text-center">
            <Form.Group className="d-flex justify-content-center align-items-center">
              <Form.Control
                variant="light"
                id="searchInput"
                placeholder={t('common:searchPlaceholder', 'Search...')}
                type="text"
                value={searchQueryModal}
                onChange={(e) => setSearchQueryModal(e.target.value)}
              />
              <Button id='ggButton' onClick={() => setIsSearchTriggered(true)}>
                <Search />
              </Button>
            </Form.Group>
          </Form>
        </div>
        <div id='fullBillFeed'>
          {renderExecutiveOrders(executiveOrdersModal, true)}
        </div>
      </Modal>

      <Modal id='fullBillModal' size="xl" show={showDocsModal} onHide={() => setShowDocsModal(false)}>
        <Modal.Header id='billModalHeader' closeButton>
          <h3>{t('common:presidentDocumentsTitle', "{{name}}'s Presidential Documents", { name })}</h3>
        </Modal.Header>
        <div id='fullBillFeed'>
          {renderPresidentialDocs(presidentialDocsModal)}
        </div>
      </Modal>

      <Modal id='fullBillModal' size="xl" show={showLawsModal} onHide={() => setShowLawsModal(false)}>
        <Modal.Header id='billModalHeader' closeButton>
          <h3>{t('common:presidentLawsTitle', "{{name}}'s Laws", { name })}</h3>
        </Modal.Header>
        <div id='fullBillFeed'>
          {renderLaws(lawsModal)}
        </div>
      </Modal>
    </div>
  );
}

export default PresidentDetailPage;